import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { isZoomApp } from '@zoom-app/helpers';
import { getCookie } from '@lib/cookie';

const app = initializeApp({
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_BUCKET,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
});

const emulatorHost = process.env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST;

// When the page is served over https (e.g. the PowerPoint add-in) the browser
// blocks the plain-http emulator as mixed content, and connectFirestoreEmulator
// always uses http. Talk to the emulator through a same-origin https path that
// the Next dev server proxies to the emulator (see rewrites in next.config.js).
// Long polling is required because the WebChannel's default streaming fetch
// doesn't survive the proxy hop.
const useHttpsEmulatorProxy =
  emulatorHost &&
  typeof window !== 'undefined' &&
  window.location.protocol === 'https:';

/** @type {import('firebase/firestore').Firestore} */
let db;
if (useHttpsEmulatorProxy) {
  console.log('Using Firestore emulator via same-origin https proxy');
  db = initializeFirestore(app, {
    host: window.location.host,
    ssl: true,
    experimentalForceLongPolling: true,
  });
} else {
  // Auto-detect long polling so Firestore falls back from streaming WebChannel to
  // long polling in environments that silently stall the stream — notably the
  // PowerPoint content add-in WebView and corporate proxies/VPNs that buffer the
  // streaming response indefinitely. Streaming can wedge half-open with no error,
  // so the SDK's reconnect never fires and onSnapshot listeners freeze until the
  // context is torn down (reopen PowerPoint). Long polling fails detectably, letting
  // the SDK recover. See local-refs/webview_issues.md and firebase-js-sdk#1674.
  // The Zoom App runs in a WebView where Firestore's streaming WebChannel wedges
  // half-open (see the comment above) and auto-detection does NOT reliably fall back,
  // so onSnapshot listeners freeze and bot-join status (hasJoinedMeeting) never
  // reaches the UI — the bot-tracker banner hangs for every bot type. Force long
  // polling there, exactly as we already do for the PowerPoint add-in.
  // Force long polling instead of the streaming WebChannel when it is likely to be
  // buffered/broken and never recover (auto-detect does not reliably fall back):
  //   - every non-production environment (uat/qa/demo/etc.) — their hosting/reverse
  //     proxy buffers the long-lived WebChannel POST, so onSnapshot listeners freeze
  //     and bot-join status never reaches the UI (the bot-tracker banner hangs). Prod
  //     hosting streams fine, so it keeps auto-detect.
  //   - the Zoom App / Electron / WebView runtimes, where the same wedge happens.
  const forceLongPolling =
    process.env.NEXT_PUBLIC_APP_ENV !== 'production'
    || (typeof window !== 'undefined' && (
      isZoomApp()
      // The PowerPoint add-in is an Office WebView with the same WebChannel wedge
      // (reported on prod). Office.js host detection isn't ready this early, so key
      // off the manifest's SourceLocation path — which covers an add-in webview's
      // FIRST load — plus the isPowerpoint cookie, which covers every later one.
      //
      // The cookie is not redundant. Entering slideshow mode reloads each content
      // add-in at its *restored* deep URL — a fresh document at, say,
      // /streams/{id}/ros, with no /entry/powerpoint hop and no _host_Info on the
      // URL. The path check is false there, so the presenting slide used to fall
      // back to the streaming WebChannel and wedge: onSnapshot never fired and the
      // interaction sat empty for the whole session while the backend kept recording
      // responses (2026-09-22 Wonder Words report — the ROS window made zero requests
      // to firestore.googleapis.com in 42 minutes). The cookie survives that reload
      // for the same reason AUTH does: the add-in webviews share a cookie jar, which
      // is what lets the deep route server-render instead of bouncing to /login.
      //
      // String() because universal-cookie JSON-parses on read, handing back the
      // boolean true rather than the string setIsPowerpoint wrote; comparing to
      // 'true' would be dead code, and plain truthiness would accept a 'false'.
      || window.location.pathname.startsWith('/entry/powerpoint')
      || String(getCookie('isPowerpoint')) === 'true'
      || /zoom|electron|webview|wv\)/i.test(window.navigator.userAgent || '')
    ));
  db = initializeFirestore(app, forceLongPolling
    ? { experimentalForceLongPolling: true }
    : { experimentalAutoDetectLongPolling: true });
  if (emulatorHost) {
    console.log('Using Firestore emulator');
    connectFirestoreEmulator(db, emulatorHost, 8080);
  }
}

const realTimeDb = getDatabase(app);
const storage = getStorage();
const auth = getAuth(app);

export default db;

export { storage, realTimeDb, auth };
